import { createStore } from "zustand/vanilla";
import { useStoreWithEqualityFn } from "zustand/traditional";
import type { Account, RoleType, Team, Tenant, User } from "@repo/types";
import { userIdGetter } from "@/lib/database";
import { apiService } from "@/services/apiService";
import { tenantRepo } from "@/repositories/tenantRepo";
import { firstValueFrom, Subscription } from "rxjs";
import { teamRepo } from "@/repositories/teamRepo";
import { userRepo } from "@/repositories/userRepo";
import { accountRepo } from "@/repositories/accountRepo";
import { baseRepo } from "@/repositories/baseRepo";

export interface UserState {
  // User
  user: User | undefined;
  id: string;
  account: Account | undefined;
  setAccount: (account: Account | undefined) => void;
  setUser: (user: User) => Promise<void>;

  // Tenants
  availableTenants: Tenant[];
  setAvailableTenants: (tenants: Tenant[]) => void;
  activeTenant: Tenant | undefined;
  setActiveTenant: (tenant: Tenant | null | undefined) => Promise<void>;

  // Teams
  availableTeams: Team[];
  setAvailableTeams: (teams: Team[]) => void;
  activeTeam: Team | undefined | "org";
  setActiveTeam: (team: Team | undefined | "org") => void;

  // Roles
  availableRoles: string[];
  setAvailableRoles: (roles: string[]) => void;
  activeRole: RoleType | undefined;
  setActiveRole: (role: RoleType) => void;
  hasMinRole: (role: RoleType) => boolean;

  // Reset
  resetUser: () => void;
}

const roles: RoleType[] = ["none", "view", "submit", "edit", "admin"];

export const roleAllowed = ({
  required,
  current,
}: {
  required: RoleType;
  current: RoleType;
}) => {
  return roles.indexOf(current) >= roles.indexOf(required);
};

const getHighestRole = (availableRoles: RoleType[]) =>
  availableRoles[availableRoles.length - 1];

const availableRolesAllow = (
  availableRoles: RoleType[],
  required: RoleType
) => {
  const current = getHighestRole(availableRoles);
  return current ? roleAllowed({ current, required }) : false;
};

const getAvailableRoles = (
  userId: string,
  team: Team | undefined,
  tenant: Tenant | undefined
) => {
  const activeRoles: RoleType[] = [];

  if (!team && tenant) {
    activeRoles.push("none");
    if (tenant.noneUsers?.includes(userId)) {
      return activeRoles;
    }
    activeRoles.push("view");
    if (tenant.viewUsers.includes(userId)) {
      return activeRoles;
    }
    activeRoles.push("submit");
    if (tenant.submitUsers.includes(userId)) {
      return activeRoles;
    }
    activeRoles.push("edit");
    if (tenant.editUsers.includes(userId)) {
      return activeRoles;
    }
    activeRoles.push("admin");
    if (tenant.adminUsers.includes(userId)) {
      return activeRoles;
    }
    return [];
  }

  if (team) {
    activeRoles.push("none");
    if (team.noneUsers?.includes(userId)) {
      return activeRoles;
    }
    activeRoles.push("view");
    if (team.viewUsers.includes(userId)) {
      return activeRoles;
    }
    activeRoles.push("submit");
    if (team.submitUsers.includes(userId)) {
      return activeRoles;
    }
    activeRoles.push("edit");
    if (team.editUsers.includes(userId)) {
      return activeRoles;
    }
    activeRoles.push("admin");
    if (team.adminUsers.includes(userId)) {
      return activeRoles;
    }
    return [];
  }

  return activeRoles;
};

const userCanUseFullOrganization = (userId: string, tenant: Tenant) =>
  availableRolesAllow(getAvailableRoles(userId, undefined, tenant), "view");

const getFirstTeamWithMinRole = (
  userId: string,
  teams: Team[],
  tenant: Tenant,
  minRole: RoleType
) =>
  teams.find((team) =>
    availableRolesAllow(getAvailableRoles(userId, team, tenant), minRole)
  );

const sortTeamsByName = (teams: Team[]) =>
  [...teams].sort(
    (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)
  );

export const userHasTeamMinRole = (
  userId: string,
  team: Team,
  tenant: Tenant,
  minRole: RoleType
) => availableRolesAllow(getAvailableRoles(userId, team, tenant), minRole);

const getTeamsWithMinRole = (
  userId: string,
  teams: Team[],
  tenant: Tenant,
  minRole: RoleType
) =>
  sortTeamsByName(
    teams.filter((team) => userHasTeamMinRole(userId, team, tenant, minRole))
  );

export const LAST_TENANT_KEY = "civi-docs-lastTenant";
export const LAST_TEAM_KEY = "civi-docs-lastTeam";
export const LAST_ROLE_KEY = "civi-docs-lastRole";

export const getLastTeamKey = (tenantId: string) =>
  `${LAST_TEAM_KEY}:${tenantId}`;

const getLastTeamId = (tenantId: string) =>
  localStorage.getItem(getLastTeamKey(tenantId)) ??
  localStorage.getItem(LAST_TEAM_KEY);

export const setLastTeamId = (tenantId: string, teamId: string) => {
  localStorage.setItem(getLastTeamKey(tenantId), teamId);
  localStorage.setItem(LAST_TEAM_KEY, teamId);
};

const removeLastTeamId = (tenantId: string) => {
  localStorage.removeItem(getLastTeamKey(tenantId));
  localStorage.removeItem(LAST_TEAM_KEY);
};

export const userStore = createStore<UserState>()((set, get) => ({
  // User
  user: undefined,
  id: "",
  account: undefined,
  setAccount: async (account: Account | undefined) => {
    set({ account });
  },
  setUser: async (user: User) => {
    const tenants = await firstValueFrom(tenantRepo.list());

    set({ user, availableTenants: tenants });
    if (tenants.length >= 1) {
      const activeTenant = tenants.find(
        (tenant) => tenant.id === localStorage.getItem(LAST_TENANT_KEY)
      );
      if (activeTenant) return;

      get().setActiveTenant(tenants[0]);
    }
  },

  // Tenants
  availableTenants: [],
  setAvailableTenants: (tenants: Tenant[]) => {
    set({ availableTenants: tenants });
  },
  activeTenant: undefined,
  setActiveTenant: async (tenant: Tenant | null | undefined) => {
    if (!tenant) {
      set({
        activeTenant: undefined,
        activeRole: undefined,
        activeTeam: undefined,
        availableRoles: [],
        availableTeams: [],
      });
      return;
    }

    const user = get().user;
    if (!user) return;

    if (tenant.id !== get().activeTenant?.id) {
      set({
        activeRole: undefined,
        activeTeam: undefined,
        availableRoles: [],
        availableTeams: [],
      });
    }
    set({ activeTenant: tenant });
    localStorage.setItem(LAST_TENANT_KEY, tenant.id);

    // populate tenants/{tenantId}/accounts/{auth.uid} if user is new to the tenant
    await apiService.v1.accessTenant(tenant.id);

    const teams = await firstValueFrom(teamRepo.list(tenant.id));

    const userTeams = getTeamsWithMinRole(user.id, teams, tenant, "view");
    set({ availableTeams: userTeams });

    const activeTeam = userTeams.find(
      (team) => team.id === getLastTeamId(tenant.id)
    );
    if (
      activeTeam &&
      availableRolesAllow(
        getAvailableRoles(user.id, activeTeam, tenant),
        "view"
      )
    ) {
      get().setActiveTeam(activeTeam);
      return;
    }

    if (userCanUseFullOrganization(user.id, tenant)) {
      get().setActiveTeam("org");
      return;
    }

    const defaultTeam = getFirstTeamWithMinRole(
      user.id,
      userTeams,
      tenant,
      "view"
    );
    if (defaultTeam) {
      get().setActiveTeam(defaultTeam);
      return;
    }

    const availableRoles = getAvailableRoles(user.id, undefined, tenant);
    const activeRole = getHighestRole(availableRoles);
    removeLastTeamId(tenant.id);
    if (activeRole) localStorage.setItem(LAST_ROLE_KEY, activeRole);
    set({
      activeTeam: undefined,
      activeRole,
      availableRoles,
    });
  },

  // Roles
  availableRoles: [],
  setAvailableRoles: (roles: string[]) => set({ availableRoles: roles }),
  activeRole: undefined,
  setActiveRole: (role: RoleType) => {
    localStorage.setItem(LAST_ROLE_KEY, role);
    set({ activeRole: role });
  },

  // Teams
  availableTeams: [],
  setAvailableTeams: (teams: Team[]) => {
    const tenant = get().activeTenant;
    const user = get().user;
    if (!tenant || !user) return;

    const userTeams = getTeamsWithMinRole(user.id, teams, tenant, "view");
    set({ availableTeams: userTeams });

    const activeTeam = get().activeTeam;
    if (
      activeTeam &&
      activeTeam !== "org" &&
      !userTeams.some((team) => team.id === activeTeam.id)
    ) {
      get().setActiveTeam(undefined);
    }
  },
  activeTeam: "org",
  setActiveTeam: (team: Team | undefined | "org") => {
    const user = get().user;
    const tenant = get().activeTenant;
    const prevRole = get().activeRole;
    const prevTeam = get().activeTeam;

    if (!team || team === "org") {
      if (user && tenant) {
        const availableRoles = getAvailableRoles(user.id, undefined, tenant);

        if (!availableRolesAllow(availableRoles, "view")) {
          const defaultTeam = getFirstTeamWithMinRole(
            user.id,
            get().availableTeams,
            tenant,
            "view"
          );
          if (defaultTeam) {
            get().setActiveTeam(defaultTeam);
            return;
          }

          const activeRole = getHighestRole(availableRoles);
          removeLastTeamId(tenant.id);
          if (activeRole) localStorage.setItem(LAST_ROLE_KEY, activeRole);
          set({
            activeRole,
            activeTeam: undefined,
            availableRoles,
          });
          return;
        }

        setLastTeamId(tenant.id, "org");

        // always pick highest role when team is actually changing
        const activeRole =
          prevRole &&
          (prevTeam === "org" || !prevTeam) &&
          availableRoles.includes(prevRole)
            ? prevRole
            : availableRoles[availableRoles.length - 1];

        localStorage.setItem(LAST_ROLE_KEY, activeRole);
        set({
          activeRole,
          activeTeam: "org",
          availableRoles,
        });
        return;
      }
      set({
        activeRole: undefined,
        activeTeam: undefined,
        availableRoles: [],
      });
      return;
    }

    if (!user || !tenant) {
      return;
    }

    const availableRoles = getAvailableRoles(user.id, team, tenant);

    if (!availableRolesAllow(availableRoles, "view")) {
      if (userCanUseFullOrganization(user.id, tenant)) {
        get().setActiveTeam("org");
        return;
      }

      const defaultTeam = getFirstTeamWithMinRole(
        user.id,
        get().availableTeams,
        tenant,
        "view"
      );
      if (defaultTeam) {
        get().setActiveTeam(defaultTeam);
        return;
      }

      const tenantRoles = getAvailableRoles(user.id, undefined, tenant);
      const activeRole = getHighestRole(tenantRoles);
      removeLastTeamId(tenant.id);
      if (activeRole) localStorage.setItem(LAST_ROLE_KEY, activeRole);
      set({
        activeTeam: undefined,
        availableRoles: tenantRoles,
        activeRole,
      });
      return;
    }

    // always pick highest role when team is actually changing
    const activeRole =
      prevRole &&
      !!prevTeam &&
      prevTeam !== "org" &&
      prevTeam.id === team.id &&
      availableRoles.includes(prevRole)
        ? prevRole
        : availableRoles[availableRoles.length - 1];
    setLastTeamId(tenant.id, team.id);
    localStorage.setItem(LAST_ROLE_KEY, activeRole);
    set({
      activeTeam: team,
      availableRoles,
      activeRole,
    });
  },
  hasMinRole: (role: RoleType) => {
    const { activeRole } = get();

    return activeRole
      ? roleAllowed({ current: activeRole, required: role })
      : false;
  },

  // Reset
  resetUser: () =>
    set({
      user: undefined,
      activeRole: undefined,
      activeTeam: "org",
      activeTenant: undefined,
      availableRoles: [],
      availableTeams: [],
      availableTenants: [],
      id: "",
    }),
}));

export function useUserStore(): UserState;

export function useUserStore<T>(
  selector: (state: UserState) => T,
  equals?: (a: T, b: T) => boolean
): T;

export function useUserStore<T>(
  selector?: (state: UserState) => T,
  equalityFn?: (a: T, b: T) => boolean
) {
  return useStoreWithEqualityFn(userStore, selector!, equalityFn);
}

userIdGetter.get = () => userStore.getState()?.id || undefined;

baseRepo.getUserId = () => userStore.getState()?.id || undefined;

// subscribe to repos

tenantRepo.list().subscribe((tenants) => {
  userStore.getState().setAvailableTenants(tenants);
});

let teamsSubscription: Subscription | null = null;
userStore.subscribe((state, prev) => {
  // selector
  if (
    prev.activeTenant?.id === state.activeTenant?.id &&
    prev.user?.id === state.user?.id
  ) {
    return;
  }

  const tenantId = state.activeTenant?.id;
  const userId = state.user?.id;

  teamsSubscription?.unsubscribe();
  teamsSubscription =
    tenantId && userId
      ? teamRepo.list(tenantId).subscribe((teams) => {
          teams = teams.filter((team) => team.allUsers.includes(userId));
          userStore.getState().setAvailableTeams(teams);
        })
      : null;

  if (!teamsSubscription) {
    userStore.getState().setAvailableTeams([]);
  }
});

let userSubscription: Subscription | null = null;
userStore.subscribe((state, prev) => {
  if (state.id === prev.id) {
    return;
  }

  const userId = state.id;

  userSubscription?.unsubscribe();
  userSubscription = userId
    ? userRepo.get(userId).subscribe((user) => {
        if (user) userStore.getState().setUser(user);
      })
    : null;

  if (!userSubscription) {
    userStore.getState().resetUser();
  }
});

accountRepo.getMyAccount().subscribe((account) => {
  userStore.getState().setAccount(account ?? undefined);
});
